import { Pipe, PipeTransform } from '@angular/core';

import { FlexCheckInStatuses } from 'libs/domain';
import { FlexTimeActivityInstance } from 'libs/generated-grpc-ts/src/flex_time/flex_time_pb';

import { PastAssignment, MyClassSortColumns, Student } from '../types/tt-my-class.types';

@Pipe({
  name: 'studentSort',
})
export class StudentSortPipe implements PipeTransform {
  transform(
    students: Student[],
    sortColumn: MyClassSortColumns,
    sortDirection: 'asc' | 'desc' | null,
    actions: Map<string, PastAssignment[]>,
    flextime: Map<string, FlexTimeActivityInstance>,
  ): Student[] {
    if (!students || students.length === 0) {
      return [];
    }

    // append behavior, checkin, and flextime data to students
    const studentsWithActions = students.map(student => {
      const allStudentActions = actions.get(student.personHash) || [];
      const behaviorActions = allStudentActions.filter(action => action.assignmentType === 'behavior');
      const checkinActions = allStudentActions.filter(action => action.assignmentType === 'check_in');
      const studentFlextime = flextime.get(student.personHash) || null;
      return {
        ...student,
        behaviorActions: behaviorActions.length ? behaviorActions : null,
        checkinActions: checkinActions.length ? checkinActions : null,
        flextime: studentFlextime,
      };
    });

    // sorter functions with first name as a tiebreaker
    const SORTERS: Record<MyClassSortColumns, (a: any, b: any) => number> = {
      firstName: (a, b) => a.firstName.localeCompare(b.firstName),
      lastName: (a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
      checkinPill: (a, b) => {
        const hasCheckinA =
          !!a.checkinActions ||
          a.flexTimeActivity?.status === FlexCheckInStatuses.IN ||
          a.flexTimeActivity?.status === FlexCheckInStatuses.TARDY;
        const hasCheckinB =
          !!b.checkinActions ||
          b.flexTimeActivity?.status === FlexCheckInStatuses.IN ||
          b.flexTimeActivity?.status === FlexCheckInStatuses.TARDY;
        const primarySort = Number(hasCheckinB) - Number(hasCheckinA);
        return primarySort !== 0 ? primarySort : a.firstName.localeCompare(b.firstName);
      },
      flextimePill: (a, b) => {
        const hasFlextimeA = !!a.flextime;
        const hasFlextimeB = !!b.flextime;
        const primarySort = Number(hasFlextimeB) - Number(hasFlextimeA);
        return primarySort !== 0 ? primarySort : a.firstName.localeCompare(b.firstName);
      },
      flextimeName: (a, b) => {
        const nameA = a.flextime?.flexTimeActivity?.name ?? '';
        const nameB = b.flextime?.flexTimeActivity?.name ?? '';
        const primarySort = nameB.localeCompare(nameA);
        return primarySort !== 0 ? primarySort : a.firstName.localeCompare(b.firstName);
      },
      praisePill: (a, b) => {
        const countA = a.behaviorActions?.filter(action => action.data?.categoryId === 0).length || 0;
        const countB = b.behaviorActions?.filter(action => action.data?.categoryId === 0).length || 0;
        const primarySort = countB - countA;
        return primarySort !== 0 ? primarySort : a.firstName.localeCompare(b.firstName);
      },
      guidancePill: (a, b) => {
        const countA = a.behaviorActions?.filter(action => action.data?.categoryId === 1).length || 0;
        const countB = b.behaviorActions?.filter(action => action.data?.categoryId === 1).length || 0;
        const primarySort = countB - countA;
        return primarySort !== 0 ? primarySort : a.firstName.localeCompare(b.firstName);
      },
    };

    const sortKey = (sortColumn as keyof typeof SORTERS) ?? 'lastName';
    const sortFn = SORTERS[sortKey] || SORTERS.firstName;

    const sortedStudents = [...studentsWithActions].sort(sortFn);

    // Reverse if descending
    if (sortDirection === 'desc') {
      sortedStudents.reverse();
    }

    return sortedStudents;
  }
}
